/**
 * SMTP Error Details Component
 * Displays user-friendly error messages with actionable suggestions
 */
export default function SmtpErrorDetails({ error }) {
  if (!error) return null;

  const errorTypeLabels = {
    connection_timeout: '⏱️ Connection Timeout',
    authentication_failed: '🔐 Authentication Failed',
    invalid_host: '🌐 Server Not Found',
    tls_error: '🔒 SSL/TLS Error',
    connection_refused: '🚫 Connection Refused',
    unknown: '❌ Connection Error'
  };

  const errorLabel = errorTypeLabels[error.type] || errorTypeLabels.unknown;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
      {/* Error Header */}
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            {errorLabel}
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p className="font-semibold">{error.message}</p>
            {error.details && (
              <p className="mt-1">{error.details}</p>
            )}
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {error.suggestions && error.suggestions.length > 0 && (
        <div className="border-t border-red-200 pt-3">
          <p className="text-sm font-medium text-red-800 mb-2">What to try:</p>
          <ul className="list-decimal list-inside space-y-1 text-sm text-red-700">
            {error.suggestions.map((suggestion, index) => (
              <li key={index}>{suggestion}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Technical Details (Collapsible) */}
      {error.technicalDetails && (
        <details className="border-t border-red-200 pt-3">
          <summary className="cursor-pointer text-sm font-medium text-red-800 hover:text-red-900">
            Technical Details
          </summary>
          <div className="mt-2 text-xs font-mono bg-red-100 p-2 rounded border border-red-200 overflow-x-auto">
            <div className="space-y-1 text-red-900">
              {Object.entries(error.technicalDetails).map(([key, value]) => (
                <div key={key}>
                  <span className="font-semibold">{key}:</span> {value}
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(error.technicalDetails, null, 2));
            }}
            className="mt-2 text-xs text-red-700 underline hover:text-red-900"
          >
            📋 Copy technical details
          </button>
        </details>
      )}
    </div>
  );
}